// schemas/subscriberSettings.ts
import { z } from 'zod'
import { ApiSingleResponseSchema } from './api'
import type { ApiSingleResponse } from './api'

// =====================================
// SETTINGS SECTIONS SCHEMAS
// =====================================

// General Settings
export const GeneralSettingsSchema = z.object({
  timezone: z.string().default('Europe/Belgrade'),
  date_format: z.string().default('dd.MM.yyyy'),
  time_format: z.string().default('HH:mm'),
  language: z.enum(['sr', 'en']).default('sr'),
  currency: z.string().default('RSD'),
})

// Notification Settings
export const NotificationSettingsSchema = z.object({
  notify_new_user: z.boolean().default(true),
  notify_subscription_expiring: z.boolean().default(true),
  notify_monthly_report: z.boolean().default(true),
  notify_security_alerts: z.boolean().default(true),
  notify_deadline_reminders: z.boolean().default(true),
  notify_job_site_irregularities: z.boolean().default(true),
  notify_unfilled_reports: z.boolean().default(true),
  notify_monthly_completeness: z.boolean().default(true),
  notification_emails: z.array(z.string().email()).nullable().optional(),
})

// Security Settings
export const SecuritySettingsSchema = z.object({
  two_factor_required: z.boolean().default(false),
  password_expiry_days: z.number().min(0).max(365).default(90),
  session_timeout_minutes: z.number().min(5).max(1440).default(60),
  max_login_attempts: z.number().min(1).max(10).default(5),
  ip_whitelist: z.array(z.string().ip()).nullable().optional(),
  audit_log_enabled: z.boolean().default(true),
})

// Integration Settings
export const IntegrationSettingsSchema = z.object({
  api_enabled: z.boolean().default(false),
  api_key: z.string().optional(),
  webhook_url: z.union([z.string().url(), z.literal(''), z.null()]).optional(),
  webhook_events: z.array(z.string()).nullable().optional(),
  integration_slack: z.boolean().default(false),
  slack_webhook_url: z
    .union([z.string().url(), z.literal(''), z.null()])
    .optional(),
  integration_teams: z.boolean().default(false),
  teams_webhook_url: z
    .union([z.string().url(), z.literal(''), z.null()])
    .optional(),
})

// Business Settings
export const BusinessSettingsSchema = z.object({
  invoice_prefix: z.string().max(16).nullable().optional(),
  invoice_starting_number: z.number().min(1).default(1),
  invoice_footer_text: z.string().nullable().optional(),
  auto_generate_invoices: z.boolean().default(true),
  invoice_due_days: z.number().min(1).max(90).default(15),
})

// Module Settings
export const ModuleSettingsSchema = z.object({
  module_safety_enabled: z.boolean().default(true),
  module_workers_enabled: z.boolean().default(true),
  module_training_enabled: z.boolean().default(true),
  module_equipment_enabled: z.boolean().default(true),
  module_documents_enabled: z.boolean().default(true),
})

// =====================================
// MAIN SUBSCRIBER SETTINGS SCHEMA
// =====================================

export const SubscriberSettingsSchema = z.object({
  id: z.number(),
  subscriber_id: z.number(),

  // All settings sections
  ...GeneralSettingsSchema.shape,
  ...NotificationSettingsSchema.shape,
  ...SecuritySettingsSchema.shape,
  ...IntegrationSettingsSchema.shape,
  ...BusinessSettingsSchema.shape,
  ...ModuleSettingsSchema.shape,

  // Custom settings
  custom_settings: z.record(z.any()).nullable().optional(),

  // Timestamps
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
  created_by: z.number().nullable().optional(),
  updated_by: z.number().nullable().optional(),
})

export type SubscriberSettings = z.infer<typeof SubscriberSettingsSchema>
export type GeneralSettings = z.infer<typeof GeneralSettingsSchema>
export type NotificationSettings = z.infer<typeof NotificationSettingsSchema>
export type SecuritySettings = z.infer<typeof SecuritySettingsSchema>
export type IntegrationSettings = z.infer<typeof IntegrationSettingsSchema>
export type BusinessSettings = z.infer<typeof BusinessSettingsSchema>
export type ModuleSettings = z.infer<typeof ModuleSettingsSchema>

// =====================================
// API RESPONSE SCHEMAS
// =====================================

// Single settings response
export const SubscriberSettingsApiResponseSchema = ApiSingleResponseSchema(
  SubscriberSettingsSchema
)
export type SubscriberSettingsApiResponse =
  ApiSingleResponse<SubscriberSettings>

// =====================================
// UPDATE SCHEMAS
// =====================================

// Update schemas for each section
export const UpdateGeneralSettingsSchema = GeneralSettingsSchema.partial()
export const UpdateNotificationSettingsSchema =
  NotificationSettingsSchema.partial()
export const UpdateSecuritySettingsSchema = SecuritySettingsSchema.partial()
export const UpdateIntegrationSettingsSchema =
  IntegrationSettingsSchema.partial()
export const UpdateBusinessSettingsSchema = BusinessSettingsSchema.partial()
export const UpdateModuleSettingsSchema = ModuleSettingsSchema.partial()

// Combined update schema
export const UpdateSubscriberSettingsSchema = z.object({
  section: z.enum([
    'general',
    'notifications',
    'security',
    'integrations',
    'business',
    'modules',
  ]),
  settings: z.union([
    UpdateGeneralSettingsSchema,
    UpdateNotificationSettingsSchema,
    UpdateSecuritySettingsSchema,
    UpdateIntegrationSettingsSchema,
    UpdateBusinessSettingsSchema,
    UpdateModuleSettingsSchema,
  ]),
})

export type UpdateSubscriberSettings = z.infer<
  typeof UpdateSubscriberSettingsSchema
>

// =====================================
// VALIDATION UTILITIES
// =====================================

export const validateSubscriberSettings = (
  data: unknown
): SubscriberSettings => {
  return SubscriberSettingsSchema.parse(data)
}

export const validateSettingsSection = (section: string, data: unknown) => {
  const sectionSchemas = {
    general: UpdateGeneralSettingsSchema,
    notifications: UpdateNotificationSettingsSchema,
    security: UpdateSecuritySettingsSchema,
    integrations: UpdateIntegrationSettingsSchema,
    business: UpdateBusinessSettingsSchema,
    modules: UpdateModuleSettingsSchema,
  }

  const schema = sectionSchemas[section as keyof typeof sectionSchemas]
  if (!schema) {
    throw new Error(`Invalid settings section: ${section}`)
  }

  return schema.parse(data)
}

// =====================================
// HELPER SCHEMAS
// =====================================

// Timezone options
export const TimezoneSchema = z.enum([
  'UTC',
  'Europe/Belgrade',
  'Europe/London',
  'Europe/Berlin',
  'Europe/Paris',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Australia/Sydney',
])

// Date format options
export const DateFormatSchema = z.enum([
  'dd.MM.yyyy',
  'MM/dd/yyyy',
  'yyyy-MM-dd',
  'dd/MM/yyyy',
])

// Time format options
export const TimeFormatSchema = z.enum(['HH:mm', 'hh:mm a', 'HH:mm:ss'])

// Language options
export const LanguageSchema = z.enum(['sr', 'en'])

// Currency options
export const CurrencySchema = z.enum(['RSD', 'EUR', 'USD', 'GBP'])
